/**
 * Deploy base-path prefix for public assets rendered outside Next's image
 * optimizer (e.g. `/homepage-v3/logos/white/anthropic.png`).
 *
 * On AWS/ECS the discover app is served under `/discover`, so a root-relative
 * asset must be requested as `/discover/homepage-v3/...`. `next/image` applies
 * this automatically via the configured `images.path`, but raw <img> tags do
 * not — without the prefix they hit the root domain, get caught in a redirect
 * loop, and 404.
 *
 * The value comes from `NEXT_PUBLIC_DISCOVER_BASE_PATH`, injected by
 * next.config from the same `useCdnAssets` decision that drives the next/image
 * path — so Vercel/local (which also set NEXT_PUBLIC_ROOT_DOMAIN=startengine.com)
 * correctly resolve to an empty prefix.
 */
export const SE3_ASSET_PREFIX = process.env.NEXT_PUBLIC_DISCOVER_BASE_PATH || '';

/**
 * Prefix a root-relative public-asset path with the deploy base path.
 * Absolute URLs (http(s)://, //, or data:) and non-root paths are returned
 * unchanged so real CDN logo URLs from offering data are never rewritten.
 */
export function withDiscoverAssetPrefix(path?: string): string | undefined {
  if (!path) return path;
  // Leave absolute, protocol-relative (//cdn…), and data: URLs untouched so a
  // real CDN logo URL is never turned into `/discover//cdn…`.
  if (/^(?:[a-z][a-z\d+\-.]*:)?\/\//i.test(path) || path.startsWith('data:')) return path;
  if (!path.startsWith('/')) return path;
  // Idempotent: don't double-prefix a path that already carries the base path
  // (e.g. a value already resolved through PUBLIC_LOGO_BASE).
  if (SE3_ASSET_PREFIX && path.startsWith(`${SE3_ASSET_PREFIX}/`)) return path;
  return `${SE3_ASSET_PREFIX}${path}`;
}

export const withSe3AssetPrefix = withDiscoverAssetPrefix;
